<template>
  <div class="ws-page">
    <!-- Header -->
    <div class="ws-header">
      <h1 class="ws-title">Word Search</h1>
      <p class="ws-subtitle">Find and cross out all the hidden words</p>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="ws-loading">
      <div class="ws-spinner"></div>
      <p>Building puzzle…</p>
    </div>

    <!-- Game area -->
    <div v-else-if="!gameOver" class="ws-game">
      <div class="ws-layout">

        <!-- Word list sidebar -->
        <div class="ws-word-list">
          <div
            v-for="(w, i) in words"
            :key="i"
            class="ws-word-item"
            :class="{ 'ws-word-item--found': w.found, [`ws-word-item--col-${i}`]: w.found }"
          >
            <span class="ws-word-item__text">{{ w.text.toUpperCase() }}</span>
            <span v-if="w.found" class="ws-word-item__tick">✓</span>
          </div>
        </div>

        <!-- Grid -->
        <div
          class="ws-grid"
          :style="{ gridTemplateColumns: `repeat(${gridSize}, 1fr)` }"
          @mouseleave="onMouseLeave"
        >
          <div
            v-for="(cell, idx) in grid"
            :key="idx"
            class="ws-cell"
            :class="cellClasses(idx)"
            @mousedown.prevent="onCellMouseDown(idx)"
            @mouseenter="onCellMouseEnter(idx)"
            @mouseup="onCellMouseUp"
            @touchstart.prevent="onCellTouchStart(idx)"
            @touchmove.prevent="onTouchMove($event)"
            @touchend.prevent="onCellMouseUp"
          >
            <span class="ws-cell__letter">{{ cell.letter.toUpperCase() }}</span>
            <!-- Strikethrough line rendered as an overlay for found cells -->
            <div
              v-if="cell.foundWordIndex !== null"
              class="ws-cell__strike"
              :class="`ws-strike--col-${cell.foundWordIndex}`"
            ></div>
          </div>
        </div>
      </div>

      <!-- Controls -->
      <div class="ws-controls">
        <button class="ws-btn ws-btn--secondary" @click="newGame">New Game</button>
      </div>

      <!-- Wrong flash -->
      <transition name="ws-fade">
        <div v-if="showWrong" class="ws-wrong-msg">Not a hidden word - keep looking!</div>
      </transition>
    </div>

    <!-- Victory screen -->
    <div v-else class="ws-victory">
      <div class="ws-victory__icon">🎉</div>
      <h2 class="ws-victory__title">Well done!</h2>
      <p class="ws-victory__sub">You found all {{ words.length }} words!</p>
      <button class="ws-btn ws-btn--primary" @click="newGame">Play Again</button>
    </div>
  </div>
</template>

<script>
var randomWords = require("random-words");

// ── Constants ────────────────────────────────────────────────────────────────
const GRID_SIZE = 11;

// 8 directions: right, left, down, up, and four diagonals
const DIRECTIONS = [
  { dr: 0,  dc: 1  }, // →
  { dr: 0,  dc: -1 }, // ←
  { dr: 1,  dc: 0  }, // ↓
  { dr: -1, dc: 0  }, // ↑
  { dr: 1,  dc: 1  }, // ↘
  { dr: 1,  dc: -1 }, // ↙
  { dr: -1, dc: 1  }, // ↗
  { dr: -1, dc: -1 }, // ↖
];

const FILLER = "abcdefghijklmnopqrstuvwxyz";

// 12-colour palette for found words
const COLOURS = [
  "#f97316", "#22c55e", "#06b6d4", "#8b5cf6",
  "#ec4899", "#eab308", "#ef4444", "#14b8a6",
  "#a855f7", "#f59e0b", "#3b82f6", "#84cc16",
];

export default {
  name: "WordSearch",

  data() {
    return {
      loading: true,
      gridSize: GRID_SIZE,
      grid: [],       // [{ letter, foundWordIndex, wordIndex, row, col }]
      words: [],      // [{ text, found, cells: [{row,col}], dir }]
      selecting: false,
      selStart: null, // { row, col }
      selEnd: null,   // { row, col }
      gameOver: false,
      showWrong: false,
      wrongTimer: null,
    };
  },

  computed: {
    // Set of flat indices currently under the selection preview
    selectionIndices() {
      if (!this.selStart || !this.selEnd) return new Set();
      return new Set(this.getLineCells(this.selStart, this.selEnd).map((c) => c.row * this.gridSize + c.col));
    },
  },

  mounted() {
    this.newGame();
    window.addEventListener("mouseup", this.onCellMouseUp);
  },

  beforeDestroy() {
    window.removeEventListener("mouseup", this.onCellMouseUp);
    if (this.wrongTimer) clearTimeout(this.wrongTimer);
  },

  methods: {
    // ── Setup ──────────────────────────────────────────────────────────────

    newGame() {
      this.loading = true;
      this.gameOver = false;
      this.selecting = false;
      this.selStart = null;
      this.selEnd = null;
      this.showWrong = false;

      setTimeout(() => {
        this.buildPuzzle();
        this.loading = false;
      }, 80);
    },

    buildPuzzle() {
      const size = this.gridSize;

      // 1. Generate candidate words: short enough to fit in the grid
      let candidates = randomWords({ min: 14, max: 20, minLength: 3, maxLength: size - 1 });
      candidates = [...new Set(candidates)];

      // 2. Build a blank grid
      const grid = Array.from({ length: size * size }, (_, i) => ({
        letter: "",
        foundWordIndex: null,
        wordIndex: null,
        row: Math.floor(i / size),
        col: i % size,
      }));

      // 3. Place words
      const placed = [];
      for (const word of candidates) {
        if (placed.length >= 12) break;
        const result = this.placeWord(word, grid, size);
        if (result) {
          const wIdx = placed.length;
          result.cells.forEach(({ row, col }) => {
            const flat = row * size + col;
            grid[flat].wordIndex = wIdx;
          });
          placed.push({ text: word, found: false, cells: result.cells, dir: result.dir });
        }
      }

      // Need at least 6 words; retry if we can't place enough
      if (placed.length < 6) {
        this.buildPuzzle();
        return;
      }

      // 4. Fill remaining empty cells with random letters
      for (let i = 0; i < size * size; i++) {
        if (!grid[i].letter) {
          grid[i].letter = FILLER[Math.floor(Math.random() * FILLER.length)];
        }
      }

      this.grid = grid;
      this.words = placed;
    },

    placeWord(word, grid, size) {
      // Shuffle directions and try each
      const dirs = [...DIRECTIONS].sort(() => Math.random() - 0.5);
      const maxAttempts = 60;

      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const dir = dirs[attempt % dirs.length];
        const startRow = Math.floor(Math.random() * size);
        const startCol = Math.floor(Math.random() * size);

        const cells = [];
        let fits = true;

        for (let i = 0; i < word.length; i++) {
          const r = startRow + dir.dr * i;
          const c = startCol + dir.dc * i;

          if (r < 0 || r >= size || c < 0 || c >= size) { fits = false; break; }

          const flat = r * size + c;
          const existing = grid[flat].letter;
          // Cell is OK if it's empty or already has the same letter (shared crossings)
          if (existing && existing !== word[i]) { fits = false; break; }

          cells.push({ row: r, col: c });
        }

        if (fits) {
          // Write letters into the grid
          cells.forEach(({ row, col }, i) => {
            grid[row * size + col].letter = word[i];
          });
          return { cells, dir };
        }
      }

      return null; // could not place
    },

    // ── Interaction ────────────────────────────────────────────────────────

    onCellMouseDown(idx) {
      this.selecting = true;
      const cell = this.grid[idx];
      this.selStart = { row: cell.row, col: cell.col };
      this.selEnd = { row: cell.row, col: cell.col };
    },

    onCellTouchStart(idx) {
      this.onCellMouseDown(idx);
    },

    onCellMouseEnter(idx) {
      if (!this.selecting) return;
      const cell = this.grid[idx];
      this.selEnd = { row: cell.row, col: cell.col };
    },

    onCellMouseUp() {
      if (!this.selecting) return;
      this.selecting = false;

      if (this.selStart && this.selEnd) {
        this.checkSelection();
      }

      this.selStart = null;
      this.selEnd = null;
    },

    onMouseLeave() {
      // Keep the selection but freeze the end point so hover outside the grid
      // doesn't reset their progress - handled naturally since selEnd doesn't change
    },

    onTouchMove(event) {
      const touch = event.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!el) return;
      const cellEl = el.closest(".ws-cell");
      if (!cellEl) return;
      const allCells = Array.from(document.querySelectorAll(".ws-cell"));
      const idx = allCells.indexOf(cellEl);
      if (idx !== -1) this.onCellMouseEnter(idx);
    },

    // ── Selection logic ────────────────────────────────────────────────────

    /**
     * Return the cells along a straight (or diagonal) line from `from` to `to`.
     * Returns an empty array if the path is not one of the 8 valid directions.
     */
    getLineCells(from, to) {
      const dr = to.row - from.row;
      const dc = to.col - from.col;
      const len = Math.max(Math.abs(dr), Math.abs(dc)) + 1;

      // Must be horizontal, vertical, or 45° diagonal
      if (dr !== 0 && dc !== 0 && Math.abs(dr) !== Math.abs(dc)) return [];

      const stepR = dr === 0 ? 0 : dr / Math.abs(dr);
      const stepC = dc === 0 ? 0 : dc / Math.abs(dc);

      const cells = [];
      for (let i = 0; i < len; i++) {
        cells.push({ row: from.row + stepR * i, col: from.col + stepC * i });
      }
      return cells;
    },

    checkSelection() {
      const cells = this.getLineCells(this.selStart, this.selEnd);
      if (cells.length < 2) return; // single tap – ignore

      const selected = cells.map(({ row, col }) => this.grid[row * this.gridSize + col].letter).join("");
      const selectedRev = selected.split("").reverse().join("");

      const matchIdx = this.words.findIndex(
        (w) => !w.found && (w.text === selected || w.text === selectedRev)
      );

      if (matchIdx !== -1) {
        // Mark word as found
        this.words[matchIdx].found = true;
        // Mark each cell with the word's colour index
        this.words[matchIdx].cells.forEach(({ row, col }) => {
          this.grid[row * this.gridSize + col].foundWordIndex = matchIdx;
        });

        if (this.words.every((w) => w.found)) {
          setTimeout(() => { this.gameOver = true; }, 600);
        }
      } else {
        // Only flash "wrong" if the selection was long enough to be intentional
        if (cells.length >= 3) this.triggerWrong();
      }
    },

    triggerWrong() {
      this.showWrong = true;
      if (this.wrongTimer) clearTimeout(this.wrongTimer);
      this.wrongTimer = setTimeout(() => { this.showWrong = false; }, 1800);
    },

    // ── Cell classes ───────────────────────────────────────────────────────

    cellClasses(idx) {
      const cell = this.grid[idx];
      const flat = idx;
      const classes = [];

      if (cell.foundWordIndex !== null) {
        classes.push("ws-cell--found");
        classes.push(`ws-cell--col-${cell.foundWordIndex}`);
      }

      if (this.selectionIndices.has(flat)) {
        classes.push("ws-cell--selecting");
      }

      return classes;
    },
  },
};
</script>

<style scoped>
/* ── Page layout ─────────────────────────────────────────── */
.ws-page {
  max-width: 680px;
  margin: 0 auto;
  padding: 0.75rem;
  font-family: var(--font-body);
}

/* ── Header ──────────────────────────────────────────────── */
.ws-header {
  text-align: center;
  margin-bottom: 1.25rem;
}

.ws-title {
  font-family: var(--font-brand);
  font-size: 2rem;
  color: var(--color-primary-dark);
  margin: 0 0 0.25rem;
}

.ws-subtitle {
  color: var(--color-text-muted);
  font-size: 0.9rem;
  margin: 0;
}

/* ── Loading ─────────────────────────────────────────────── */
.ws-loading {
  text-align: center;
  padding: 3rem 0;
  color: var(--color-text-muted);
}

.ws-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: ws-spin 0.8s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes ws-spin {
  to { transform: rotate(360deg); }
}

/* ── Layout: word list + grid side by side ───────────────── */
.ws-layout {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

/* ── Word list ───────────────────────────────────────────── */
.ws-word-list {
  flex: 0 0 130px;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 0.65rem 0.6rem;
  box-shadow: var(--shadow-md);
}

.ws-word-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.2rem 0.45rem;
  border-radius: 6px;
  transition: background 0.25s ease, opacity 0.25s ease;
  background: rgba(0, 0, 0, 0.04);
}

.ws-word-item__text {
  font-family: var(--font-brand);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--color-text);
  transition: color 0.25s ease;
}

.ws-word-item--found {
  opacity: 0.55;
}

/* Strikethrough text when found */
.ws-word-item--found .ws-word-item__text {
  text-decoration: line-through;
  text-decoration-thickness: 2px;
}

.ws-word-item__tick {
  font-size: 0.85rem;
  color: #16a34a;
  margin-left: 4px;
  flex-shrink: 0;
}

/* Word item accent colours when found */
.ws-word-item--col-0  { background: rgba(249,115,22,0.12);  }
.ws-word-item--col-1  { background: rgba(34,197,94,0.12);   }
.ws-word-item--col-2  { background: rgba(6,182,212,0.12);   }
.ws-word-item--col-3  { background: rgba(139,92,246,0.12);  }
.ws-word-item--col-4  { background: rgba(236,72,153,0.12);  }
.ws-word-item--col-5  { background: rgba(234,179,8,0.12);   }
.ws-word-item--col-6  { background: rgba(239,68,68,0.12);   }
.ws-word-item--col-7  { background: rgba(20,184,166,0.12);  }
.ws-word-item--col-8  { background: rgba(168,85,247,0.12);  }
.ws-word-item--col-9  { background: rgba(245,158,11,0.12);  }
.ws-word-item--col-10 { background: rgba(59,130,246,0.12);  }
.ws-word-item--col-11 { background: rgba(132,204,22,0.12);  }

/* ── Grid ────────────────────────────────────────────────── */
.ws-grid {
  flex: 1 1 auto;
  display: grid;
  gap: 3px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  padding: 0.4rem;
  user-select: none;
  touch-action: none;
}

/* ── Cells ───────────────────────────────────────────────── */
.ws-cell {
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  background: #dde1e7;
  transition: background-color 0.2s ease, transform 0.1s ease;
}

.ws-cell:hover {
  background: #cdd3db;
}

.ws-cell__letter {
  font-family: var(--font-brand);
  font-size: clamp(0.65rem, 2.2vw, 0.9rem);
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
  color: var(--color-text);
  transition: color 0.2s ease;
  position: relative;
  z-index: 1;
}

/* Selecting (dragging over) state */
.ws-cell--selecting {
  background: #bfdbfe !important;
  transform: scale(1.06);
  z-index: 2;
}

.ws-cell--selecting .ws-cell__letter {
  color: #1e40af;
}

/* Found state: coloured background + white letter */
.ws-cell--found .ws-cell__letter { color: #fff; }

.ws-cell--col-0  { background: #f97316 !important; }
.ws-cell--col-1  { background: #22c55e !important; }
.ws-cell--col-2  { background: #06b6d4 !important; }
.ws-cell--col-3  { background: #8b5cf6 !important; }
.ws-cell--col-4  { background: #ec4899 !important; }
.ws-cell--col-5  { background: #eab308 !important; color: #1e293b; }
.ws-cell--col-6  { background: #ef4444 !important; }
.ws-cell--col-7  { background: #14b8a6 !important; }
.ws-cell--col-8  { background: #a855f7 !important; }
.ws-cell--col-9  { background: #f59e0b !important; }
.ws-cell--col-10 { background: #3b82f6 !important; }
.ws-cell--col-11 { background: #84cc16 !important; color: #1e293b; }

/* col-5 and col-11 have a light background so darken the letter */
.ws-cell--col-5 .ws-cell__letter,
.ws-cell--col-11 .ws-cell__letter { color: #1e293b; }

/* Strikethrough overlay on found cells */
.ws-cell__strike {
  position: absolute;
  inset: 50% 0;
  height: 3px;
  border-radius: 2px;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 2;
  opacity: 0.55;
}

/* Strike colours matching found word */
.ws-strike--col-0  { background: #fff; }
.ws-strike--col-1  { background: #fff; }
.ws-strike--col-2  { background: #fff; }
.ws-strike--col-3  { background: #fff; }
.ws-strike--col-4  { background: #fff; }
.ws-strike--col-5  { background: #78350f; }
.ws-strike--col-6  { background: #fff; }
.ws-strike--col-7  { background: #fff; }
.ws-strike--col-8  { background: #fff; }
.ws-strike--col-9  { background: #fff; }
.ws-strike--col-10 { background: #fff; }
.ws-strike--col-11 { background: #365314; }

/* ── Controls ────────────────────────────────────────────── */
.ws-controls {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.ws-btn {
  padding: 0.55rem 1.4rem;
  border: none;
  border-radius: 50px;
  font-family: var(--font-brand);
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ws-btn:active { transform: scale(0.96); }

.ws-btn--primary {
  background: var(--color-primary);
  color: #fff;
  box-shadow: var(--shadow-sm);
}

.ws-btn--primary:hover { background: var(--color-primary-dark); }

.ws-btn--secondary {
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  box-shadow: var(--shadow-sm);
}

.ws-btn--secondary:hover { background: var(--color-primary-light); }

/* ── Wrong message ───────────────────────────────────────── */
.ws-wrong-msg {
  text-align: center;
  background: #fee2e2;
  color: #b91c1c;
  border-radius: var(--radius-sm);
  padding: 0.5rem 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  margin-top: 0.5rem;
}

.ws-fade-enter-active,
.ws-fade-leave-active { transition: opacity 0.3s ease; }
.ws-fade-enter, .ws-fade-leave-to { opacity: 0; }

/* ── Victory ─────────────────────────────────────────────── */
.ws-victory {
  text-align: center;
  padding: 2.5rem 1rem;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
}

.ws-victory__icon { font-size: 3.5rem; margin-bottom: 0.5rem; }

.ws-victory__title {
  font-family: var(--font-brand);
  font-size: 1.8rem;
  color: var(--color-primary-dark);
  margin: 0 0 0.4rem;
}

.ws-victory__sub {
  color: var(--color-text-muted);
  margin: 0 0 1.5rem;
}

/* ── Responsive: stack on small screens ──────────────────── */
@media (max-width: 600px) {
  .ws-page {
    padding: 0.5rem;
  }

  .ws-title {
    font-size: 1.5rem;
  }

  .ws-layout {
    flex-direction: column-reverse;
    gap: 0.65rem;
  }

  /* Grid takes up the full width so cells are as large as possible */
  .ws-grid {
    width: 100%;
    box-sizing: border-box;
    gap: 2px;
    padding: 0.3rem;
  }

  /* Bigger letters — 11-column grid on ~390px screen → each cell ≈35px → letter ~0.95rem */
  .ws-cell__letter {
    font-size: clamp(0.72rem, 7vw / 11, 1rem);
  }

  /* Word list becomes a horizontal pill-cloud beneath the grid */
  .ws-word-list {
    flex: 0 0 auto;
    flex-direction: row;
    flex-wrap: wrap;
    width: 100%;
    box-sizing: border-box;
    gap: 0.35rem;
    padding: 0.5rem;
  }

  .ws-word-item {
    flex: 0 0 auto;
    padding: 0.25rem 0.55rem;
  }

  .ws-word-item__text {
    font-size: 0.8rem;
  }
}
</style>
